import { useLocalStorageState, usePageContext, withResolvedHref } from '@faro/app-context';
import {
    FaroMenu,
    FaroNavigationSectionLabel,
    FaroNavigationTree,
    FaroOverlay,
    scrollToElement,
} from '@faro/design-system';
import { useTenantConfigurationQuery } from '@faro/study-designer-services';
import React, { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CreateNewUserLinkModal } from '../../../../study-designer/component/StudyDefinitionNav/CreateNewUserLinkForm/CreateNewUserLinkModal';
import UserLinkNav, { UserLink } from '../../../../study-designer/component/StudyDefinitionNav/UserLinkNav/UserLinkNav';
import { getVisibleSpacePageSections } from '../../spaces/[id]/spacePageSectionsData';
import styles from './SpacePageSideBar.module.scss';

const NavigationTreeNodeWithResolvedHref = withResolvedHref(FaroNavigationTree.Node);

export interface SpacePageSideBarProps {
    loading?: boolean;
    outlet?: ReactNode;
}

function SpacePageSideBar(props: SpacePageSideBarProps): React.JSX.Element {
    const { outlet } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const { activeSectionId, setActiveSectionIdPauseScrollSpy } = usePageContext();
    const { data } = useTenantConfigurationQuery();
    const [labsLinkModalOpen, setLabsLinkModalOpen] = useState<boolean>(false);

    const pathname = location.pathname;
    const labLinksEnabled = data?.labLinksEnabled ?? false;
    const protocolOptimizationEnabled = data?.protocolOptimizationEnabled === true;
    const visibleSpacePageSections = getVisibleSpacePageSections(protocolOptimizationEnabled);
    const LOCAL_STORAGE_KEY = `labLinks`;
    const [labLinks, setLabLinks] = useLocalStorageState<UserLink[]>(LOCAL_STORAGE_KEY, []);

    const deleteUserLink = (id: string) => {
        setLabLinks(labLinks.filter(link => link.id !== id));
    };

    const addUserLink = (userLink: UserLink) => {
        const newLinks = [...labLinks, { id: userLink.id, label: userLink.label, url: userLink.url }];
        setLabLinks(newLinks);
    };

    const generalInformationControls = labLinksEnabled ? (
        <FaroNavigationTree.NodeMenu>
            <FaroMenu.Item onClick={() => setLabsLinkModalOpen(true)}>Add Lab Links</FaroMenu.Item>
        </FaroNavigationTree.NodeMenu>
    ) : null;

    return (
        <>
            {outlet != null ? (
                outlet
            ) : (
                <div className={styles.spacePageNavLinks}>
                    <FaroNavigationSectionLabel>Study Space</FaroNavigationSectionLabel>
                    <FaroNavigationTree fullWidth selection={activeSectionId}>
                        {visibleSpacePageSections.map(section => (
                            <NavigationTreeNodeWithResolvedHref
                                key={section.id}
                                id={section.id}
                                icon={section.icon}
                                label={section.label}
                                controls={
                                    section.id === 'general-information' && labLinksEnabled
                                        ? generalInformationControls
                                        : undefined
                                }
                                onSelect={() => {
                                    navigate(`${pathname}#${section.id}`);
                                    setActiveSectionIdPauseScrollSpy(section.id);
                                    scrollToElement(document.querySelector<HTMLElement>(`section[id="${section.id}"]`));
                                }}
                                collapseDisabled
                                data-automation-id={section.id}
                                to={`${location.pathname}${location.search}${location.hash}`}
                            />
                        ))}
                        {labLinksEnabled && labLinks.length > 0 && (
                            <>
                                <FaroNavigationTree.NodeHeader>Labs</FaroNavigationTree.NodeHeader>
                                {labLinks.map(link => (
                                    <UserLinkNav key={link.id} userLink={link} onDelete={deleteUserLink} />
                                ))}
                            </>
                        )}
                    </FaroNavigationTree>
                    <FaroOverlay
                        container={document.body}
                        open={labsLinkModalOpen}
                        onClose={() => setLabsLinkModalOpen(false)}
                    >
                        <CreateNewUserLinkModal
                            onSave={link => {
                                addUserLink(link);
                                setLabsLinkModalOpen(false);
                            }}
                            onClose={() => setLabsLinkModalOpen(false)}
                        />
                    </FaroOverlay>
                </div>
            )}
        </>
    );
}

export default SpacePageSideBar;
